import { CheckCircle, Play, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface PolicyTestResult {
  policy_name: string;
  matched: boolean;
  action: string;
  reason?: string;
}

interface PolicyTestProps {
  onClose: () => void;
}

export function PolicyTester({ onClose }: PolicyTestProps) {
  const [testInput, setTestInput] = useState({
    package_name: 'my-package',
    package_size: '1048576',
    package_visibility: 'public',
    package_ecosystem: 'npm',
    user_role: 'member',
    request_method: 'GET',
  });
  const [testResults, setTestResults] = useState<PolicyTestResult[]>([]);
  const [testing, setTesting] = useState(false);

  const runTest = async () => {
    setTesting(true);

    // Simulate policy testing
    setTimeout(() => {
      const results: PolicyTestResult[] = [
        {
          policy_name: 'Require Authentication for Private Packages',
          matched: testInput.package_visibility === 'private',
          action: testInput.package_visibility === 'private' ? 'require_auth' : 'skip',
          reason: testInput.package_visibility === 'private' ? 'Package is private' : 'Package is public',
        },
        {
          policy_name: 'Block Large Package Uploads',
          matched: parseInt(testInput.package_size, 10) > 104857600,
          action: parseInt(testInput.package_size, 10) > 104857600 ? 'deny' : 'allow',
          reason:
            parseInt(testInput.package_size, 10) > 104857600
              ? 'Package exceeds size limit'
              : 'Package within size limit',
        },
        {
          policy_name: 'Security Scanning',
          matched: true,
          action: 'log',
          reason: 'All uploads are logged for security',
        },
      ];

      setTestResults(results);
      setTesting(false);
    }, 1000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Test Policies</h2>
            <Button variant="ghost" onClick={onClose}>
              ×
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Test Input</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Package Name</label>
                  <input
                    type="text"
                    value={testInput.package_name}
                    onChange={(e) =>
                      setTestInput({
                        ...testInput,
                        package_name: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Package Size (bytes)</label>
                  <input
                    type="number"
                    value={testInput.package_size}
                    onChange={(e) =>
                      setTestInput({
                        ...testInput,
                        package_size: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Package Visibility</label>
                  <select
                    value={testInput.package_visibility}
                    onChange={(e) =>
                      setTestInput({
                        ...testInput,
                        package_visibility: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="public">Public</option>
                    <option value="private">Private</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ecosystem</label>
                  <select
                    value={testInput.package_ecosystem}
                    onChange={(e) =>
                      setTestInput({
                        ...testInput,
                        package_ecosystem: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="npm">NPM</option>
                    <option value="pypi">PyPI</option>
                    <option value="cargo">Cargo</option>
                    <option value="maven">Maven</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">User Role</label>
                  <select
                    value={testInput.user_role}
                    onChange={(e) => setTestInput({ ...testInput, user_role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="admin">Admin</option>
                    <option value="member">Member</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Request Method</label>
                  <select
                    value={testInput.request_method}
                    onChange={(e) =>
                      setTestInput({
                        ...testInput,
                        request_method: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="DELETE">DELETE</option>
                  </select>
                </div>
              </div>

              <Button onClick={runTest} disabled={testing} className="flex items-center gap-2">
                <Play className="w-4 h-4" />
                {testing ? 'Testing...' : 'Run Test'}
              </Button>
            </CardContent>
          </Card>

          {testResults.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Test Results</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {testResults.map((result, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {result.matched ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-gray-400" />
                        )}
                        <div>
                          <p className="text-sm font-medium">{result.policy_name}</p>
                          <p className="text-xs text-gray-600">{result.reason}</p>
                        </div>
                      </div>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          result.action === 'allow'
                            ? 'bg-green-100 text-green-800'
                            : result.action === 'deny'
                              ? 'bg-red-100 text-red-800'
                              : result.action === 'require_auth'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {result.action}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t p-6">
          <div className="flex justify-end">
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
